import { Box, Button, FormHelperText } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useLoadOptions } from 'src/hooks/useLoadOptions'
import CardActions from '../../../components/CardActions'
import {
  useLCSLHPTIConcentracionTipos,
  useLCSLHPTILimiteAgregadoTipos,
  useLCSLHPTILimiteHoldingTipos,
  useLCSLHPTILimitePerdidaTipos,
  useLCSLHPTILimiteTipos
} from '../hooks/useLCSLHPTICache'
import {
  LCSLHPTIGroupedHeader,
  LCSLHPTIRow,
  pivotLCSLHPTIData,
  useLCSLHPTITablaValorInicial
} from '../hooks/useLCSLHPTITablaValor'
import { OperacionLCSLHPTIFields, OperacionLCSLHPTITablaValorFields } from '../types/fields'
import { FormOperacionLCSLHPTI } from '../types/types'
import { VALOR_GUION } from '../constants'
import LCSLHPTITablaValoresEditorGrid from './LCSLHPTITablaValoresEditorGrid'

interface SeccionNuevoLCSLHPTITablaValoresProps {
  formMethods: UseFormReturn<FormOperacionLCSLHPTI>
  codSistemaPrincipal: number
}

const conOpcionGuion = <T extends { id: number; descripcion: string }>(data: T[] | undefined, extra: Partial<T> = {}): T[] => {
  return [{ id: 0, descripcion: VALOR_GUION, ...extra } as T, ...(data || [])];
};

const esVacio = (valor: unknown): boolean => valor === null || valor === undefined || valor === '';

const SeccionNuevoLCSLHPTITablaValores = ({ formMethods, codSistemaPrincipal }: SeccionNuevoLCSLHPTITablaValoresProps) => {
  const [pivotData, setPivotData] = useState<{
    groupedHeaders: LCSLHPTIGroupedHeader[],
    rows: LCSLHPTIRow[],
  }>({ groupedHeaders: [], rows: [] });

  const { setValue, getValues, control } = formMethods;

  const { valores: tablaValorOriginal, isLoading } = useLCSLHPTITablaValorInicial(codSistemaPrincipal);

  const { limiteTipos, isLoading: ldL, isError: erL, error: eL } = useLCSLHPTILimiteTipos();
  const { limiteHoldingTipos, isLoading: ldH, isError: erH, error: eH } = useLCSLHPTILimiteHoldingTipos();
  const { concentracionTipos, isLoading: ldC, isError: erC, error: eC } = useLCSLHPTIConcentracionTipos();
  const { limitePerdidaTipos, isLoading: ldP, isError: erP, error: eP } = useLCSLHPTILimitePerdidaTipos();
  const { limiteAgregadoTipos, isLoading: ldAG, isError: erAG, error: eAG } = useLCSLHPTILimiteAgregadoTipos();

  const limiteTiposConGuion = useMemo(() => conOpcionGuion(limiteTipos, { monto: 0 } as any), [limiteTipos]);
  const limiteHoldingTiposConGuion = useMemo(() => conOpcionGuion(limiteHoldingTipos), [limiteHoldingTipos]);
  const concentracionTiposConGuion = useMemo(() => conOpcionGuion(concentracionTipos), [concentracionTipos]);
  const limitePerdidaTiposConGuion = useMemo(() => conOpcionGuion(limitePerdidaTipos), [limitePerdidaTipos]);
  const limiteAgregadoTiposConGuion = useMemo(() => conOpcionGuion(limiteAgregadoTipos, { monto: 0 } as any), [limiteAgregadoTipos]);

  const loadLimite = useLoadOptions({ data: limiteTiposConGuion, loading: ldL, isError: erL, error: eL });
  const loadHolding = useLoadOptions({ data: limiteHoldingTiposConGuion, loading: ldH, isError: erH, error: eH });
  const loadConcentracion = useLoadOptions({ data: concentracionTiposConGuion, loading: ldC, isError: erC, error: eC });
  const loadPerdida = useLoadOptions({ data: limitePerdidaTiposConGuion, loading: ldP, isError: erP, error: eP });
  const loadLimiteAgregado = useLoadOptions({ data: limiteAgregadoTiposConGuion, loading: ldAG, isError: erAG, error: eAG });


  const selectoresMapping = {
    limite_credito: loadLimite,
    limite_holding: loadHolding,
    concentracion: loadConcentracion,
    limite_perdida: loadPerdida,
    limite_agregado: loadLimiteAgregado
  };

  useEffect(() => {
    if (tablaValorOriginal.length > 0) {
      setValue(OperacionLCSLHPTIFields.TablaValores, tablaValorOriginal as any);
      const p = pivotLCSLHPTIData(tablaValorOriginal);
      setPivotData({ groupedHeaders: p.groupedHeaders, rows: p.rows });
    }
  }, [tablaValorOriginal, setValue]);

  const rellenarTablaAutomaticamente = () => {
    const valoresActuales = (getValues(OperacionLCSLHPTIFields.TablaValores) || []) as any[];

    const nuevosValores = valoresActuales.map((celda: any) =>
      esVacio(celda[OperacionLCSLHPTITablaValorFields.Valor])
        ? { ...celda, [OperacionLCSLHPTITablaValorFields.Valor]: VALOR_GUION }
        : celda
    );

    setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, { shouldDirty: true });

    if (esVacio(getValues(OperacionLCSLHPTIFields.LimiteAgregadoPosicionComprada))) {
      setValue(OperacionLCSLHPTIFields.LimiteAgregadoPosicionComprada, VALOR_GUION, { shouldDirty: true });
    }

    if (esVacio(getValues(OperacionLCSLHPTIFields.LimiteAgregadoPosicionVendida))) {
      setValue(OperacionLCSLHPTIFields.LimiteAgregadoPosicionVendida, VALOR_GUION, { shouldDirty: true });
    }
  };

  const agregarFilaOtros = () => {
    const nuevoRowId = `temp-${Date.now()}`;
    const actuales = (getValues(OperacionLCSLHPTIFields.TablaValores) || []) as any[];

    const nuevasCeldas = pivotData.groupedHeaders.flatMap(group =>
      group.columnas.map(col => {
        const ref = actuales.find(v => v[OperacionLCSLHPTITablaValorFields.CodOperacionTipo] === col.codOperacionTipo);
        
return {
          [OperacionLCSLHPTITablaValorFields.CodInstrumento]: 15,
          [OperacionLCSLHPTITablaValorFields.DescInstrumento]: "Otros",
          [OperacionLCSLHPTITablaValorFields.CodOperacionTipo]: col.codOperacionTipo,
          [OperacionLCSLHPTITablaValorFields.DescOperacionTipo]: col.descOperacionTipo,
          [OperacionLCSLHPTITablaValorFields.Agrupacion]: group.agrupacion,
          [OperacionLCSLHPTITablaValorFields.Valor]: "",
          [OperacionLCSLHPTITablaValorFields.Orden]: 99,
          [OperacionLCSLHPTITablaValorFields.PermiteEditarInstrumento]: true,
          [OperacionLCSLHPTITablaValorFields.TipoSelector]: ref?.[OperacionLCSLHPTITablaValorFields.TipoSelector] || "",
          rowId: nuevoRowId,
        };
      })
    );

    const nuevosValores = [...actuales, ...nuevasCeldas];
    setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, { shouldDirty: true });

    const p = pivotLCSLHPTIData(nuevosValores);
    setPivotData({ groupedHeaders: p.groupedHeaders, rows: p.rows });
  };


  const eliminarFilaOtros = (rowKey: string | number) => {
    const actuales = getValues(OperacionLCSLHPTIFields.TablaValores) || [];
    const nuevosValores = actuales.filter((v: any) =>
      (v.rowId || v[OperacionLCSLHPTITablaValorFields.CodInstrumento]) !== rowKey
    );

    setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, { shouldDirty: true });
    const p = pivotLCSLHPTIData(nuevosValores);
    setPivotData({ groupedHeaders: p.groupedHeaders, rows: p.rows });
  };

  return (
    <CardActions title="Matriz de Límites y Condiciones" icon='mdi:table-edit' colapsable tableContent>
      <Box sx={{ px: 4, pt: 3, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="secondary" onClick={rellenarTablaAutomaticamente}>
          Rellenar valores automáticamente
        </Button>
      </Box>

      <LCSLHPTITablaValoresEditorGrid
        formMethods={formMethods}
        groupedHeaders={pivotData.groupedHeaders}
        rows={pivotData.rows}
        isLoading={isLoading}
        selectoresMapping={selectoresMapping}
        onAgregarOtros={agregarFilaOtros}
        onEliminarOtros={eliminarFilaOtros}
        permiteEditar={true}
      />

      {formMethods.formState.errors.tablaValores?.message && (
        <Box sx={{ px: 4, mt: 2 }}>
          <FormHelperText error>
            {formMethods.formState.errors.tablaValores.message}
          </FormHelperText>
        </Box>
      )}
    </CardActions>
  );
}

export default SeccionNuevoLCSLHPTITablaValores;
